import { CustomFetchOptions, Status } from "~/types/CustomFetch";

export function useCustomFetch(
  url: string | URL,
  options?: CustomFetchOptions
) {
  const loading = ref(false);
  const status: Ref<Status> = ref("idle");
  const data: Ref<any> = ref();

  const headers = computed(() => new Headers(options?.headers));

  const body = computed(
    () =>
      options?.body &&
      Object.fromEntries(
        Object.entries(options.body).map(([k, v]) => [
          k,
          Object.hasOwn(v, "_value") ? v.value : v,
        ])
      )
  );

  const queryString = computed(() => {
    if (options?.query && Object.keys(options?.query).length) {
      return `?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(options.query).map(([k, q]) => [
            k,
            Object.hasOwn(q, "_value") ? q.value ?? "" : q ?? "",
          ])
        )
      )}`;
    }

    return "";
  });

  const update = async () => {
    status.value = "pending";
    loading.value = true;

    return fetch(`${url}${queryString.value}`, {
      method: options?.method,
      headers: headers.value,
      body: JSON.stringify(body.value),
    })
      .then((response) => response.json())
      .then((responseData) => {
        status.value = "success";
        loading.value = false;

        data.value = responseData;
        options?.onResponse?.(responseData);
      })
      .catch((error) => {
        status.value = "error";
        loading.value = false;

        options?.onError?.(error);
      });
  };

  let debounceTimer: any;

  if (options?.watch) {
    watch(options.watch, () => {
      if (options?.debounce) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(update, options.debounce);
      } else {
        update();
      }
    });
  }

  onBeforeMount(async () => {
    if (options?.immediate) {
      update();
    }
  });

  return {
    loading,
    status,
    update,
    data,
  };
}
